// Remember list filters / paging / scroll across navigation (sessionStorage, per browser tab) — 2026-09-06
// Back from a record returns to the list exactly as it was.
import { useCallback, useEffect, useRef, useState } from 'react';

const read = (ns: string): Record<string, any> => { try { return JSON.parse(sessionStorage.getItem(`list.${ns}`) || '{}'); } catch { return {}; } };
const write = (ns: string, patch: Record<string, any>) => { try { sessionStorage.setItem(`list.${ns}`, JSON.stringify({ ...read(ns), ...patch })); } catch { /* ignore */ } };

export function usePersistedState<T>(
  ns: string,
  key: string,
  initial: T,
  codec?: { serialize: (v: T) => any; deserialize: (raw: any) => T },
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    const s = read(ns);
    if (!(key in s)) return initial;
    try { return codec ? codec.deserialize(s[key]) : (s[key] as T); } catch { return initial; }
  });
  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      write(ns, { [key]: codec ? codec.serialize(next) : next });
      return next;
    });
  }, [ns, key, codec]);
  return [value, set];
}

/** Save the scroll position while the user scrolls; restore it once after `ready` first becomes true. */
export function useScrollMemory(ns: string, ready: boolean) {
  const restored = useRef(false);
  useEffect(() => {
    let t: any;
    const onScroll = () => { clearTimeout(t); t = setTimeout(() => write(ns, { __scrollY: window.scrollY }), 150); };
    window.addEventListener('scroll', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, [ns]);
  useEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;
    const y = Number(read(ns).__scrollY || 0);
    if (y > 0) {
      let tries = 0;
      const tick = () => { window.scrollTo({ top: y }); if (Math.abs(window.scrollY - y) > 2 && tries++ < 10) setTimeout(tick, 150); };
      setTimeout(tick, 50);
    }
  }, [ns, ready]);
}
